import { useEffect, useState } from "react";
import { Trash2, Eraser } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner, EmptyState } from "@/components/shared";
import { toast } from "sonner";

const Row = ({ title, sub, onDelete, testId }) => (
  <div className="p-4 border-b border-[#E3E8E8] last:border-0 flex items-center justify-between gap-3" data-testid={testId}>
    <div className="min-w-0"><p className="font-semibold text-[#0B1733] truncate">{title}</p><p className="text-xs text-[#4A5568] truncate">{sub}</p></div>
    {onDelete && <Button size="sm" variant="outline" onClick={onDelete} data-testid={`${testId}-delete`} className="rounded-full text-red-600 border-red-200 hover:bg-red-50 shrink-0"><Trash2 size={14} className="mr-1" /> Verwijder</Button>}
  </div>
);

const List = ({ title, items, filter, render }) => {
  const shown = items.filter((i) => JSON.stringify(i).toLowerCase().includes(filter.toLowerCase()));
  return (
    <section className="min-w-0">
      <h3 className="font-display font-bold text-lg text-[#0B1733] mb-3">{title} ({shown.length})</h3>
      {shown.length === 0 ? <EmptyState title="Niets gevonden" /> : <div className="bg-white rounded-2xl border border-[#E3E8E8] overflow-hidden max-h-[480px] overflow-y-auto">{shown.map(render)}</div>}
    </section>
  );
};

export const AdminData = ({ companies, onChanged }) => {
  const [users, setUsers] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [filter, setFilter] = useState("");
  const load = () => {
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => setUsers([]));
    api.get("/admin/jobs").then((r) => setJobs(r.data)).catch(() => setJobs([]));
  };
  useEffect(() => { load(); }, []);

  const del = async (path, msg) => {
    if (!window.confirm(msg)) return;
    try { await api.delete(path); toast.success("Verwijderd"); load(); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const purge = async () => {
    try {
      const { data: p } = await api.get("/admin/test-data");
      const total = p.users.length + p.companies.length + p.jobs.length + p.posts.length;
      if (!total) { toast("Geen testgegevens gevonden"); return; }
      const list = [...p.users, ...p.companies, ...p.jobs, ...p.posts].slice(0, 15).map((x) => `• ${x.label}`).join("\n");
      if (!window.confirm(`Dit verwijdert ${p.users.length} accounts, ${p.companies.length} bedrijven, ${p.jobs.length} jobs en ${p.posts.length} berichten:\n\n${list}${total > 15 ? "\n…" : ""}\n\nDoorgaan?`)) return;
      const { data: c } = await api.post("/admin/test-data/purge");
      toast.success(`Gewist: ${c.users} accounts, ${c.companies} bedrijven, ${c.jobs} jobs, ${c.posts} berichten`);
      load(); onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!users) return <Spinner />;
  return (
    <div className="space-y-6" data-testid="admin-data-panel">
      <div className="bg-white rounded-2xl border border-[#E3E8E8] p-5 flex flex-wrap items-center justify-between gap-4">
        <div className="max-w-xl">
          <p className="font-display font-bold text-[#0B1733]">Alle testgegevens wissen</p>
          <p className="text-sm text-[#4A5568]">Verwijdert accounts, bedrijven, jobs en berichten met "test" in de naam/e-mail of een @example.com-adres. Je ziet eerst wat er weg gaat. Admin-accounts blijven altijd staan.</p>
        </div>
        <Button onClick={purge} data-testid="purge-test-data-btn" className="rounded-full bg-red-600 hover:bg-red-700 text-white"><Eraser size={15} className="mr-1.5" /> Testgegevens wissen</Button>
      </div>
      <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Zoek op naam, e-mail of titel…" data-testid="admin-data-filter" className="max-w-sm" />
      <div className="grid lg:grid-cols-3 gap-6">
        <List title="Accounts" items={users} filter={filter} render={(u) => (
          <Row key={u.id} testId={`admin-user-${u.id}`} title={u.name || u.email} sub={`${u.email} · ${u.role}`}
            onDelete={u.role === "admin" ? null : () => del(`/admin/users/${u.id}`, `Account ${u.email} verwijderen? Ook hun jobs, bedrijven, berichten en reacties gaan weg.`)} />
        )} />
        <List title="Jobs" items={jobs} filter={filter} render={(j) => (
          <Row key={j.id} testId={`admin-job-${j.id}`} title={j.title} sub={`${j.owner_name || "—"} · ${j.service_name || ""} · ${j.status}`}
            onDelete={() => del(`/admin/jobs/${j.id}`, `Job "${j.title}" verwijderen, inclusief offertes en berichten?`)} />
        )} />
        <List title="Bedrijven" items={companies || []} filter={filter} render={(c) => (
          <Row key={c.id} testId={`admin-company-row-${c.id}`} title={c.trading_name} sub={`${c.status} · ${c.business_email || ""}`}
            onDelete={() => del(`/admin/companies/${c.id}`, `Bedrijf "${c.trading_name}" verwijderen, inclusief logo, licentie, offertes en reviews?`)} />
        )} />
      </div>
    </div>
  );
};
